import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import   pointsWithinPolygon   from '@turf/points-within-polygon'; // brackets left off on purpose
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';
import * as d3 from 'd3';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

declare var L;


/*
 * data-layer-geojson-point-fit defines a class to display geojson point data associated with Flood Hazard Inventory Tool (FHIT).
 *
 * --------------------------------
 *
 * Within the configuration file, point stations can be included:
 * "overlayLayers": [
			{"id": "fhit5", "label": "FHIT live", 
				"url": "https://services3.arcgis.com/TCnvslgqrzhT2ZXG/ArcGIS/rest/services/2021_FHIT_Database_Some_Attachments/FeatureServer/{layerId}/query?where=1%3D1&outFields=*&returnGeometry=true&returnExceededLimitFeatures=true&sqlFormat=none&f=pgeojson",
				"layerIds": [0,1,2,3], "class": "data-layer-geojson-point-fhit",
				"titleString": "Name", "basins": ["map/overlay-layers/basins.geojson"],
				"DWSType": "FhitNovaScoreMax",
				"popupTitle": "Structure_ID,Name,Owner,Structure_Subype,Flood_Risk",
				"legendUrl": "assets/images/FHITLegend.png",
				"clusterMarkers": true, "refresh": true },
 * 		{"id": "fhitPointLayer", "label": "FHIT", "url": "map/overlay-layers/fhitstructures.geojson",
 *		 "class": "data-layer-geojson-point-fhit", "refresh": false },
 * ...
 * ]
 * 
 * Key components of the feature file retrieved above are:
 * {
  "type" : "FeatureCollection", 
  "features" : [
    {
      "type" : "Feature", 
      "id" : 1, 
      "geometry" : 
      {
        "type" : "Point", 
        "coordinates" : [
          -104.884767798502, 39.1506795264627
        ]
      }, 
      "properties" : {
        "Structure_ID" : "PCR-RC-001", 
        "Name" : "Railroad at Carpenter Creek", 
        "Flood_Risk" : "500-Year", 
      }
    }, ...
 *
 * 	Cluster icon resources
 *		https://github.com/Leaflet/Leaflet.markercluster/blob/master/example/geojson.html 
 *		http://bl.ocks.org/gisminister/10001728  - really great pie ring around the marker example 
	styleUrls: ['app.component.css', 'data-layer-cluster.css']
 */

@Component({
	selector: 'data-layer-geojson-point-fhit',
	template: `
		<div class="checkboxDiv">
			<label class="overlay-layer"><input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
			<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPointFHITComponent extends DataLayerGeojsonPointComponent {

    basinFilenames: any[] = [];
    basinGeojsons: any[] = [];
    DWSType;
    DWSstatisticType;
	DWSduration;
	layerIds = null;
	novaScoreMap;

	constructor(
        private http: HttpClient,
		mapService: MapService, dataLayerService: DataLayerService, configService: ConfigService, userMessageService: UserMessageService ) {
		super(mapService, dataLayerService, configService, userMessageService);
		this.setupNovaScoreMap();
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
            // first, read in boundary files if this is the first time this layer has been activated
            if ( this.basinGeojsons.length === 0 ) {
                const observableArray = [];
                for ( let i = 0; i < this.basinFilenames.length; i++ ) {
                    observableArray.push ( this.http.get ( this.configService.baseDir + this.basinFilenames[i], {responseType: 'text'} ));
                }
                const observableGeojsons = forkJoin(observableArray);
                observableGeojsons.subscribe ({
                    next: value => { 
                        for ( let i = 0; i < value.length; i++ ) {
                            // console.log('Value from observableGeojsons', value[i]);
                            this.basinGeojsons.push( JSON.parse ( <any>value[i] ));
                        }
                    },
                    complete: () => { this.activateLayer();}
                })

            } else {
			    this.activateLayer();
            }
		} else {
			this.deactivateLayer();
		}

	}

	setData ( someData: any ) {
        super.setData(someData);
        
		if ( someData.hasOwnProperty('basins')) {
			this.basinFilenames = someData.basins;
        }
		if ( someData.hasOwnProperty('DWSType')) {
			const parts = someData.DWSType.split(',');
			
			this.DWSType = parts.length > 0 ? parts[0] : 'MeanArealPrecip';
			this.DWSstatisticType = parts.length > 1 ? parts[1] : 'Last';
			this.DWSduration = parts.length > 2 ? parts[2] : '1hour';
		}
		if ( someData.hasOwnProperty('layerIds')) {
			this.layerIds = someData.layerIds;
		}

	}

	placeMarkers(feature, latlng) {
		// const color = this.convertScoreToColor( feature.properties.stationSummary.dwsNovaScore );
		// const textColor = this.convertScoreToTextColor( feature.properties.stationSummary.dwsNovaScore );
		// Determine the color and text color of the marker based on the novascore.
		const dwsNS = feature.properties.ns;
		let color = 'white';
		let textColor = 'black';
		if ( dwsNS === -1 ) {
			color = 'blue-dark';
			textColor = 'white';
		} else if ( dwsNS ===  0 ) {
			color = 'black';
			textColor = 'white';
		} else if ( dwsNS === 1 || dwsNS ===  14 ) {
			color = 'green';
			textColor = 'white';
		} else if ( dwsNS === 2 || dwsNS ===  15 ) {
			color = 'yellow';
		} else if ( dwsNS === 3 || dwsNS ===  16 ) {
			color = 'orange';
		} else if ( dwsNS === 4 || dwsNS ===  17 ) {
			color = 'pink';
		} else if ( dwsNS === 5 || dwsNS ===  18 ) {
			color = 'red';
			textColor = 'white';
		} 


		// console.log(feature.properties.titleString);
		const ts = feature.properties.titleString != null ? feature.properties[feature.properties.titleString] : null;
		const value = ts != null ? ts : feature.properties.id != null ? feature.properties.id : feature.properties.name;
		if ( value == null ) {
			// console.log(feature);
		}

		// if there are currently alarms, set class to flashing
		// This may need to change (stationSummary)
		var alarms = feature.properties.hasOwnProperty ( feature.properties.alarms ) ? feature.properties['alarms'] : [];
		var alarmClasses = '';
		/* Indicate if this station is in an alarm status */
		if ( alarms.length > 0 ) {
			alarms.forEach ( alarm => {
				if ( alarm.alarmState === 'Active' ) {
					alarmClasses = 'alarm-icon blinking';
				}
			});
		}

		let title;
		let marker;

		if ( value === null || value === 'NR' || value === 'OOS' || dwsNS === 1 || dwsNS === 14) {
			if ( value === 'OOS' ) {
				marker = L.icon({
					iconUrl: 'assets/images/icon-png-1circle-blue.png',
					iconSize: [20, 20],
					iconAnchor: [10, 10]
				});
			} else if ( dwsNS === 1 || dwsNS === 14 ) {
				marker = L.icon({
					iconUrl: 'assets/images/icon-png-1circle-green-black.png',
					iconSize: [20, 20],
					iconAnchor: [10, 10]
				});
			} else {
				marker = L.icon({
					iconUrl: 'assets/images/icon-png-1circle-black.png',
					iconSize: [20, 20],
					iconAnchor: [10, 10]
				});
			}
			// marker.options.shadowSize = [50, 75]; // ?
		} else {
			title = value;
			marker = L.ExtraMarkers.icon({
				extraClasses: alarmClasses,
				icon: 'fa-number',
				iconColor: textColor,		// text color
				markerColor: color,		// marker background color
				shape: 'square',
				prefix: 'fa',
				number: title
			});
			marker.options.shadowSize = [50, 75];
		}

		return L.marker(latlng, {icon: marker});  // http://stackoverflow.com/questions/29080194/leaflet-adding-an-offset-to-marker
	}

	// assign popup information
	onEachPointFeature(feature, layer) {
		const stationName = feature.properties['name'] != null ? feature.properties['name'] : 
			feature.properties['Name'] != null ? feature.properties['Name'] : '';
		const stationDescription = feature.properties['description'] != null ? feature.properties['description'] : 
			feature.properties['Description'] ? feature.properties['Description'] : '';
		let popupContent, tooltipContent;
		let options;

		// Assign HTML content to use in a popup when the user clicks on a marker
		// console.log(this['contents']);
		if ( this['contents'].popupTitle != null ) {
			// Assign HTML content to use in a popup when the user clicks on a feature
			// Multiple properties may be displayed.  They are specified with a comma-separated string.
			const props = this['contents'].popupTitle.split(',');
			let popupContent = '<h2>' + feature.properties[props[0]]  + '</h2>';
			for ( let i = 1; i < props.length; i++ ) {
				popupContent += '<h4>' + props[i] +  ': ' + feature.properties[props[i]]  + '</h4>';
			}
			// console.log(popupContent);

			if (feature.properties && feature.properties.popupContent) {
				// This will append content from a "popupContent" property if it is in the GeoJSON file
				popupContent += feature.properties.popupContent;
			}

			layer.bindPopup(popupContent);
		}

		tooltipContent = stationName + ' (click for more info)';
		layer.bindTooltip(tooltipContent);
	}

	// parseDataFile parses the fhit file given in the configuration  file to retrieve the fhit structures,
	// parses the DWS file for all MeanArealPrecip stations,
	// filters each according to basins given, and assigns the novascore from the DWS station to the fhit stations.
	parseDataFile () {
		console.log('In data-layer-geojson-point-fhit.parseDataFile.');
		const self = this;
		console.log(this.titleString);

		// filenames keeps track of url requests, httpGetCommands are the actual http.get commands
		const filenames = [];
		const httpGetCommands = [];
		if ( this.layerIds == null ) {
			const filename = this.url.includes('http') ? this.url : this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
			console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id + ' stations.');
			filenames.push(filename);
			httpGetCommands.push(this.http.get(filename));
		} else {
			for ( let i = 0; i < this.layerIds.length; i++ ) {
				let filename = this.url.includes('http') ? this.url : this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
				filename = filename.replace('{layerId}', this.layerIds[i]);
				filenames.push(filename);
				httpGetCommands.push(this.http.get(filename).pipe ( catchError ( error => of(null))));
				console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id + ' stations.');
			}
		}
		const observable = forkJoin(httpGetCommands);

		// first, get all geojson structure file content
		observable.subscribe({ 
			next: allFHITStationsArray => {

				// compile all the layers into a single object, concatening all the 'features' into one
				let allFHITStations = null;
				for ( let i = 0; i < allFHITStationsArray.length; i++ ) {
					if ( allFHITStationsArray[i] != null ) {
						console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
							filenames[i] + ' for all ' + self.id + ' stations was successfully completed (' +
							allFHITStationsArray[i]['features'].length + ' features).');
						if ( allFHITStations == null ) {
							allFHITStations = allFHITStationsArray[i];
						} else {
							allFHITStations['features'] = allFHITStations['features'].concat(allFHITStationsArray[i]['features']);
						}
					}
				}

				// console.log(allFHITStations);

				// Set a few basics straight with the fhit data
				// First, remove any features with geometry set to null
				allFHITStations['features'] = allFHITStations['features'].filter(function(f) { return f.geometry != null });

				for ( let i = 0; i < allFHITStations['features'].length; i++ ) {
					// make sure fhit structure has property ns, even though it will initially be set to null
					const fhit = allFHITStations['features'][i];
					if ( !fhit.properties.hasOwnProperty('ns')) {
						fhit.properties.ns = null;
					}
					fhit.properties.titleString = self.titleString;
				}
				// console.log(allFHITStations);
				console.log('FHIT stations retrieved');

				// get all the station summaries from DWS
				const stationSummariesURI = self.configService.createURI ( { 'filename': 'stationSummaries.geojson' });
                self.http.get ( stationSummariesURI ).subscribe ( allStations => {

					console.log('All stations of type ' + self.DWSType + ' have been retrieved.');
					// filter the features to only include those containing dataTypes defined for the layer
					allStations['features'] = allStations['features'].filter(function(s) {
						const dt = s.properties.stationSummary.dataTypes;
						const dt_filtered = dt.filter(function (d) { return d.name === self.DWSType });
						return dt_filtered.length === 0 ? false : true; });

					// At this point, allStations.features contains only MeanArealPrecip stations
					// Need to set fhit station novascores based on DWS MeanAreaPrecip station in the same basin
					// console.log('Number of basin files: ' + self.basinGeojsons.length );
                	for ( let i = 0; i < self.basinGeojsons.length; i++) {
						const basinGeojson = self.basinGeojsons[i];
						// console.log( allFHITStations );
						// console.log( basinGeojson );

						for ( let f = 0; f < basinGeojson.features.length; f++ ) {

							const singleBasin = basinGeojson.features[f];


							// allStationsFiltered are all the stations within the current basin
							// console.log(allStations);
							const allStationsFiltered = pointsWithinPolygon( <any>allStations, singleBasin );

							if ( allStationsFiltered.features.length > 0 ) {
								const tmpStatistic = allStationsFiltered.features[0].properties.stationSummary.statistics.filter(function(stat) {
									return stat.dataType === self.DWSType && stat.statisticType === self.DWSstatisticType && stat.duration.toUpperCase() === self.DWSduration.toUpperCase();
								});

								if ( tmpStatistic.length > 0 ) {
									const s = tmpStatistic[0];
									console.log('Retrieved fhit novascore of ' + s.novaScore);

									// FHITstationsFiltered are all fhit features within the current basin
									// console.log(allStations);
									const FHITstationsFiltered = pointsWithinPolygon( <any>allFHITStations, singleBasin);
									// console.log( FHITstationsFiltered );

									const mapping = this.novaScoreMap.get(s.novaScore);

									// determine the novascore for each feature
									for ( let j = 0; j < FHITstationsFiltered.features.length; j++) {
										// FHITstationsFiltered.features[j].properties.ns = s.novaScore;
										FHITstationsFiltered.features[j].properties.ns = this.getFeatureNovaScore (
											mapping, FHITstationsFiltered.features[j].properties.Flood_Risk );
									}
								}
							} else {
								// slows the display too much?
								// console.log( "No stations found within basin specified."); // need more descriptive location
							}
						}
					}

					self.value = allFHITStations;
					self.valueObservable.next(allFHITStations);
					/*
					console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
						filename + ' for all ' + self.id + ' stations was successfully completed.');
						*/
					// console.log ( dataLayer );
				});
			}
			// error => { console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + filename + ' for all ' + self.id + ' stations was UNSUCCESSFUL.'); }
		});
	}

	// Map the station novascore to a novascore for each individual feature based on its Flood_Risk
	// novaScoreMap is a Map of Maps
	setupNovaScoreMap() {

		const fiveYearMap = new Map();
		fiveYearMap.set ('2-Year', 5 );
		fiveYearMap.set ('5-Year', 2 );

		const tenYearMap = new Map();
		tenYearMap.set ('2-Year', 5 );
		tenYearMap.set ('5-Year', 5 );
		tenYearMap.set ('10-Year', 2 );
		tenYearMap.set ('25-Year', 2 );

		const twentyfiveYearMap = new Map();
		twentyfiveYearMap.set ('2-Year', 5 );
		twentyfiveYearMap.set ('5-Year', 5 );
		twentyfiveYearMap.set ('10-Year', 5 );
		twentyfiveYearMap.set ('25-Year', 2 );
		twentyfiveYearMap.set ('50-Year', 2 );

		const fiftyYearMap = new Map();
		fiftyYearMap.set ('2-Year', 5 );
		fiftyYearMap.set ('5-Year', 5 );
		fiftyYearMap.set ('10-Year', 5 );
		fiftyYearMap.set ('25-Year', 5 );
		fiftyYearMap.set ('50-Year', 2 );
		fiftyYearMap.set ('100-Year', 2 );
		fiftyYearMap.set ('200-Year', 2 );
		fiftyYearMap.set ('500-Year', 2 );

		const onehundredYearMap = new Map();
		onehundredYearMap.set ('2-Year', 5 );
		onehundredYearMap.set ('5-Year', 5 );
		onehundredYearMap.set ('10-Year', 5 );
		onehundredYearMap.set ('25-Year', 5 );
		onehundredYearMap.set ('50-Year', 5 );
		onehundredYearMap.set ('100-Year', 2 );
		onehundredYearMap.set ('200-Year', 2 );
		onehundredYearMap.set ('500-Year', 2 );

		const twohundredYearMap = new Map();
		twohundredYearMap.set ('2-Year', 5 );
		twohundredYearMap.set ('5-Year', 5 );
		twohundredYearMap.set ('10-Year', 5 );
		twohundredYearMap.set ('25-Year', 5 );
		twohundredYearMap.set ('50-Year', 5 );
		twohundredYearMap.set ('100-Year', 5 );
		twohundredYearMap.set ('200-Year', 2 );
		twohundredYearMap.set ('500-Year', 2 );

		this.novaScoreMap = new Map();
		this.novaScoreMap.set (	7, fiveYearMap );
		this.novaScoreMap.set ( 8, tenYearMap );
		this.novaScoreMap.set ( 9, twentyfiveYearMap );
		this.novaScoreMap.set ( 10, fiftyYearMap );
		this.novaScoreMap.set ( 11, onehundredYearMap );
		this.novaScoreMap.set ( 12, twohundredYearMap );
		this.novaScoreMap.set ( 13, twohundredYearMap );
		this.novaScoreMap.set ( 14, twohundredYearMap );
	}

	// given the mapping for the station novascore and Flood_Risk from the feature, determine the novascore for the feature
	// if anything is undefined (mapping, floodRisk, or the results of getting the maping for the floodRisk) return 1;
	getFeatureNovaScore (mapping, floodRisk ) {
		if ( mapping === undefined ) { return 1; }
		if ( floodRisk === null ) { return 1; }
		const ns = mapping.get(floodRisk);
		return ns === undefined ? 1 : ns;
	}
}
